import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme, type ColorTheme, type BackgroundGradient, type HeaderLayout, type HeaderStyle, type SidebarLayout, type SidebarStyle } from '../contexts/ThemeContext';

const Personalization: React.FC = () => {
    const {
        colorTheme,
        backgroundGradient,
        darkMode,
        headerLayout,
        headerStyle,
        sidebarLayout,
        sidebarStyle,
        direction,
        backgroundImage,
        setColorTheme,
        setBackgroundGradient,
        setDarkMode,
        setHeaderLayout,
        setHeaderStyle,
        setSidebarLayout,
        setSidebarStyle,
        setDirection,
        setBackgroundImage,
    } = useTheme();

    const colorThemes: { name: string; value: ColorTheme; className: string }[] = [
        { name: 'Default', value: 'default', className: 'bg-default' },
        { name: 'Blue', value: 'blue', className: 'bg-blue' },
        { name: 'Indigo', value: 'indigo', className: 'bg-indigo' },
        { name: 'Purple', value: 'purple', className: 'bg-purple' },
        { name: 'Pink', value: 'pink', className: 'bg-pink' },
        { name: 'Red', value: 'red', className: 'bg-red' },
        { name: 'Orange', value: 'orange', className: 'bg-orange' },
        { name: 'Yellow', value: 'yellow', className: 'bg-yellow' },
        { name: 'Green', value: 'green', className: 'bg-green' },
        { name: 'Teal', value: 'teal', className: 'bg-teal' },
        { name: 'Pista', value: 'pista', className: 'bg-pista' },
        { name: 'Cyan', value: 'cyan', className: 'bg-cyan' },
        { name: 'Grey', value: 'grey', className: 'bg-grey' },
        { name: 'Brown', value: 'brown', className: 'bg-brown' },
        { name: 'Choco', value: 'chocolate', className: 'bg-chocolate' },
        { name: 'Black', value: 'black', className: 'bg-dark' },
    ];

    const sidebarStyles: { name: string; value: SidebarStyle; className: string }[] = [
        { name: 'None', value: 'transparent', className: 'bg-default' },
        { name: 'White', value: 'white', className: darkMode ? 'bg-black' : 'bg-white' },
        { name: 'BgTheme', value: 'bg', className: 'bg-body-1' },
        { name: 'Theme', value: 'theme', className: 'bg-theme-1' },
        { name: 'Accent', value: 'accent', className: 'bg-theme-accent-1' },
    ];

    const sidebarLayouts: { name: string; value: SidebarLayout; icon: string }[] = [
        { name: 'Standard', value: 'standard', icon: 'bi-arrow-clockwise' },
        { name: 'Iconic', value: 'iconic', icon: 'bi-bezier' },
        { name: 'Boxed', value: 'boxed', icon: 'bi-box' },
    ];

    const headerStyles: { name: string; value: HeaderStyle; className: string }[] = [
        { name: 'None', value: 'transparent', className: 'bg-default' },
        { name: 'White', value: 'white', className: darkMode ? 'bg-black' : 'bg-white' },
        { name: 'BgTheme', value: 'bg', className: 'bg-body-1' },
        { name: 'Theme', value: 'theme', className: 'bg-theme-1' },
        { name: 'Accent', value: 'accent', className: 'bg-theme-accent-1' },
    ];

    const headerLayouts: { name: string; value: HeaderLayout; icon: string }[] = [
        { name: 'None', value: 'standard', icon: 'bi-arrow-clockwise' },
        { name: 'Boxed', value: 'boxed', icon: 'bi-box' },
    ];

    return (
        <>
            {/* page title */}
            <div className="container py-3">
                <div className="row align-items-center page-title">
                    <div className="col col-sm mb-3 mb-sm-0">
                        <h6 className="mb-0">Personalization</h6>
                        <p className="text-secondary small">Customize color scheme and global structure</p>
                    </div>
                </div>
                <nav aria-label="breadcrumb" className="breadcrumb-theme bg-theme-1-subtle px-2 py-1 mt-0 mt-sm-3 rounded">
                    <ol className="breadcrumb mb-0">
                        <li className="breadcrumb-item bi"><Link to="/app/dashboard"><i className="bi bi-house-door me-2"></i> DevUIUX</Link></li>
                        <li className="breadcrumb-item bi active" aria-current="page">Personalization</li>
                    </ol>
                </nav>
            </div>

            {/* content */}
            <div className="container" id="main-content">

                <div className="row gx-3">
                    <div className="col-12 col-lg-6 mb-3 mb-lg-4 mb-xl-5 pt-1">

                        <h2 className="fw-bold mb-1">Theme Color</h2>
                        <h4 className="opacity-75 fw-normal">Choose your desired theme color</h4>
                        <p className="small opacity-75">Theme colors in web design are pivotal as they influence user emotions, perceptions, and actions.Theme colors in web design are pivotal as they influence user emotions, perceptions, and actions.</p>

                        <div className="row gx-3 theme-select">
                            {colorThemes.map((theme) => (
                                <div key={theme.value} className="col-auto">
                                    <div
                                        className={`select-box text-center mb-2 ${colorTheme === theme.value ? 'active' : ''}`}
                                        onClick={() => setColorTheme(theme.value)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <span className={`avatar avatar-40 rounded-circle mb-2 ${theme.className}`}>
                                            {theme.value === 'default' && <i className="bi bi-arrow-clockwise"></i>}
                                        </span>
                                        <p className="fs-12 opacity-50">{theme.name}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="col-12 col-lg-6">
                        <div className="card mb-3 mb-lg-4 mb-xl-5">
                            <div className="card-body">
                                <h2 className="fw-bold mb-1">Background Color</h2>
                                <h4 className="opacity-75 fw-normal">Choose your desired theme color</h4>
                                <p className="small opacity-75 mb-3 mb-lg-4">The chosen background and text colors should have sufficient contrast to ensure readability for users with visual impairments. There are classes which will be useful like None, white, bg-r-gradient, bg-gradient-1 to 10</p>
                                <div className="row gx-3 theme-background">
                                    {[
                                        { value: 'bg-default' as BackgroundGradient, label: 'None', hasIcon: true },
                                        { value: 'bg-white' as BackgroundGradient, label: 'White', hasIcon: false },
                                        { value: 'bg-r-gradient' as BackgroundGradient, label: 'Theme', hasIcon: false },
                                        { value: 'bg-gradient-1' as BackgroundGradient, label: 'Grad-1', hasIcon: false },
                                        { value: 'bg-gradient-2' as BackgroundGradient, label: 'Grad-2', hasIcon: false },
                                        { value: 'bg-gradient-3' as BackgroundGradient, label: 'Grad-3', hasIcon: false },
                                        { value: 'bg-gradient-4' as BackgroundGradient, label: 'Grad-4', hasIcon: false },
                                        { value: 'bg-gradient-5' as BackgroundGradient, label: 'Grad-5', hasIcon: false },
                                        { value: 'bg-gradient-6' as BackgroundGradient, label: 'Grad-6', hasIcon: false },
                                        { value: 'bg-gradient-7' as BackgroundGradient, label: 'Grad-7', hasIcon: false },
                                        { value: 'bg-gradient-8' as BackgroundGradient, label: 'Grad-8', hasIcon: false },
                                        { value: 'bg-gradient-9' as BackgroundGradient, label: 'Grad-9', hasIcon: false },
                                        { value: 'bg-gradient-10' as BackgroundGradient, label: 'Grad-10', hasIcon: false },
                                    ].map((gradient) => (
                                        <div key={gradient.value} className="col-auto">
                                            <div
                                                className={`gradient-box text-center mb-2 ${backgroundGradient === gradient.value ? 'active' : ''}`}
                                                onClick={() => setBackgroundGradient(gradient.value)}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <span className={`avatar avatar-40 rounded-circle mb-2 ${gradient.value}`}>
                                                    {gradient.hasIcon && <i className="bi bi-arrow-clockwise"></i>}
                                                </span>
                                                <p className="fs-12 opacity-50">{gradient.label}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row gx-3 align-items-center">
                    <div className="col-12 col-lg-6 mb-3">
                        <h2 className="fw-bold mb-1">Background Image</h2>
                        <h4 className="opacity-75 fw-normal">Create depth with images & blur effect</h4>
                        <p className="small opacity-75 mb-3">The chosen background and text colors should have sufficient contrast to ensure readability for users with visual impairments. There are classes which will be useful like None, white, bg-r-gradient, bg-gradient-1 to 10 </p>
                    </div>
                    <div className="col-12 col-lg-12 mb-3 mb-lg-4 mb-xl-5">
                        <div className="row gx-3 theme-background">
                            <div className="col-auto mb-3">
                                <div
                                    className={`select-box text-center ${backgroundImage === 'bg-none' ? 'active' : ''}`}
                                    data-title="bg-none"
                                    onClick={() => setBackgroundImage('bg-none')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <span className="avatar avatar-80 rounded mb-2">
                                        <i className="bi bi-arrow-clockwise"></i>
                                    </span>
                                    <p className="fs-12 opacity-50">None</p>
                                </div>
                            </div>
                            {[
                                { id: 1, src: '/assets/img/background-image/backgorund-image-1.jpg' },
                                { id: 2, src: '/assets/img/background-image/backgorund-image-2.jpg' },
                                { id: 3, src: '/assets/img/background-image/backgorund-image-3.jpg' },
                                { id: 4, src: '/assets/img/background-image/backgorund-image-4.jpg' },
                                { id: 5, src: '/assets/img/background-image/backgorund-image-5.jpg' },
                                { id: 6, src: '/assets/img/background-image/backgorund-image-7.jpg' },
                                { id: 7, src: '/assets/img/background-image/backgorund-image-8.jpg' },
                                { id: 8, src: '/assets/img/background-image/backgorund-image-9.jpg' },
                                { id: 9, src: '/assets/img/background-image/backgorund-image-10.jpg' },
                                { id: 10, src: '/assets/img/background-image/backgorund-image-11.jpg' },
                                { id: 11, src: '/assets/img/background-image/backgorund-image-12.jpg' },
                            ].map((img) => (
                                <div className="col-auto mb-3" key={img.id}>
                                    <div
                                        className={`select-box text-center ${backgroundImage === asset(img.src) ? 'active' : ''}`}
                                        onClick={() => setBackgroundImage(asset(img.src))}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <span className="avatar avatar-70 rounded coverimg mb-2">
                                            <img src={asset(img.src)} alt="" className="w-100" />
                                        </span>
                                        <p className="fs-12 opacity-50">Bg {img.id}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="row gx-3 gx-lg-5 align-items-center">
                    <div className="col-12 col-lg-6">
                        <div className="row gx-3 align-items-center">
                            <div className="col-12 col-lg-12 mb-3">
                                <h2 className="fw-bold mb-1">Sidebar</h2>
                                <h4 className="opacity-75 fw-normal">Experience with Fill color & Style</h4>
                                <p className="small opacity-75 mb-3">Take control of your dashboard! Customize sidebar color and layout to match your unique style and preferences.</p>
                            </div>
                            <div className="col-12 col-lg-12 mb-3 mb-lg-4 mb-xl-5">
                                <div className="row gx-3">
                                    <div className="col-12 col-md mb-3 mb-md-0">
                                        <h6 className="fw-normal mb-3">Color Fill</h6>
                                        <div className="row gx-3">
                                            {sidebarStyles.map((style) => (
                                                <div key={style.value} className="col-auto">
                                                    <div
                                                        className={`select-box text-center mb-2 ${sidebarStyle === style.value ? 'active' : ''}`}
                                                        onClick={() => setSidebarStyle(style.value)}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        <span className={`avatar avatar-40 rounded-circle mb-2 ${style.className}`}>
                                                            {style.value === 'transparent' && <i className="bi bi-arrow-clockwise"></i>}
                                                        </span>
                                                        <p className="small">{style.name}</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="col-auto">
                                        <h6 className="fw-normal mb-3">Layout Style</h6>
                                        <div className="row gx-3 sidebar-layout">
                                            {sidebarLayouts.map((layout) => (
                                                <div key={layout.value} className="col-auto">
                                                    <div
                                                        className={`select-box text-center mb-2 ${sidebarLayout === layout.value ? 'active' : ''}`}
                                                        onClick={() => setSidebarLayout(layout.value)}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        <span className="avatar avatar-40 rounded-circle mb-2 bg-default">
                                                            <i className={`bi ${layout.icon} ${layout.value !== 'standard' ? 'h4' : ''}`}></i>
                                                        </span>
                                                        <p className="small">{layout.name}</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-12 col-lg-6">
                        <div className="row gx-3 align-items-center">
                            <div className="col-12 col-lg-12 mb-3">
                                <h2 className="fw-bold mb-1">Header</h2>
                                <h4 className="opacity-75 fw-normal">Make stylish with Fill color & Style</h4>
                                <p className="small opacity-75 mb-3">Enhance your workflow with a personalized dashboard. Easily customize header color and layout to create a more efficient and enjoyable user experience.</p>
                            </div>
                            <div className="col-12 col-lg-12 mb-3 mb-lg-4 mb-xl-5">
                                <div className="row gx-3">
                                    <div className="col-12 col-md mb-3 mb-md-0">
                                        <h6 className="fw-normal mb-3">Color Fill</h6>
                                        <div className="row gx-3">
                                            {headerStyles.map((style) => (
                                                <div key={style.value} className="col-auto">
                                                    <div
                                                        className={`select-box text-center mb-2 ${headerStyle === style.value ? 'active' : ''}`}
                                                        onClick={() => setHeaderStyle(style.value)}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        <span className={`avatar avatar-40 rounded-circle mb-2 ${style.className}`}>
                                                            {style.value === 'transparent' && <i className="bi bi-arrow-clockwise"></i>}
                                                        </span>
                                                        <p className="small">{style.name}</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="col-auto">
                                        <h6 className="fw-normal mb-3">Layout Style</h6>
                                        <div className="row gx-3">
                                            {headerLayouts.map((layout) => (
                                                <div key={layout.value} className="col-auto">
                                                    <div
                                                        className={`select-box text-center mb-2 ${headerLayout === layout.value ? 'active' : ''}`}
                                                        onClick={() => setHeaderLayout(layout.value)}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        <span className="avatar avatar-40 rounded-circle mb-2 bg-default">
                                                            <i className={`bi ${layout.icon} ${layout.value === 'boxed' ? 'h5' : ''}`}></i>
                                                        </span>
                                                        <p className="small">{layout.name}</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row gx-3 gx-lg-5">
                    <div className="col-12 col-md-6 mb-4 mb-xl-5">
                        <h2 className="fw-bold mb-1">Light & Dark Mode</h2>
                        <h4 className="opacity-75 fw-normal mb-3 mb-lg-4">There is elegance in the darkness</h4>
                        <div className="w-100 height-200 rounded coverimg mb-3 mb-lg-3 mb-xl-4">
                            <img src={asset('/assets/img/adminuiux/light-dark.jpg')} alt="" className="w-100" />
                        </div>
                        <p className="small opacity-75 mb-3">Light mode Preferred in well-lit conditions for better readability and can be more accessible to users with certain visual impairments. Dark mode reduces glare and can help with reducing eye strain in low-light conditions.</p>

                        {/* light-dark layout */}
                        <div className="row align-items-center no-rtl">
                            <div className="col-auto">Light</div>
                            <div className="col-auto px-0">
                                <div className="form-check form-switch sunmoon">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        role="switch"
                                        id="btn-layout-modes-dark"
                                        checked={darkMode}
                                        onChange={(e) => setDarkMode(e.target.checked)}
                                    />
                                    <label className="form-check-label" htmlFor="btn-layout-modes-dark">
                                        <i className="bi bi-brightness-low sun"></i>
                                        <i className="bi bi-moon-stars moon"></i>
                                    </label>
                                </div>
                            </div>
                            <div className="col-auto">Dark</div>
                        </div>
                    </div>
                    <div className="col-12 col-md-6 mb-4 mb-xl-5">
                        <h2 className="fw-bold mb-1">Text Direction</h2>
                        <h4 className="opacity-75 fw-normal mb-3 mb-lg-4">Give respect to different languages</h4>
                        <div className="w-100 height-200 rounded coverimg mb-3 mb-lg-3 mb-xl-4">
                            <img src={asset('/assets/img/adminuiux/direction.jpg')} alt="" className="w-100" />
                        </div>
                        <p className="small opacity-75 mb-3">RTL (Right-to-Left) layout mode is essential for languages that are read from right to left, such as Arabic, Hebrew, and Persian. Elements like headings, paragraphs, links, images, and form elements should be auto-adjusted to rtl</p>

                        {/* rtl */}
                        <div className="row align-items-center no-rtl">
                            <div className="col-auto">LTR</div>
                            <div className="col-auto px-0">
                                <div className="form-check form-switch rtlcheck">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        role="switch"
                                        id="btn-layout-dir-rtl"
                                        checked={direction === 'rtl'}
                                        onChange={(e) => setDirection(e.target.checked ? 'rtl' : 'ltr')}
                                    />
                                    <label className="form-check-label" htmlFor="btn-layout-dir-rtl">
                                        <i className="bi bi-justify-left ltrjustify"></i>
                                        <i className="bi bi-justify-right rtljustify"></i>
                                    </label>
                                </div>
                            </div>
                            <div className="col-auto">RTL</div>
                        </div>
                    </div>
                </div>
            </div>


        </>
    );
};

export default Personalization;
